import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import CronJobForm from './CronJobForm'; // Import the separated form
import axios from 'axios';
import {
    Box,
    Heading,
    Text,
    Button,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tag,
    Input,
    InputGroup,
    InputLeftElement,
    useDisclosure,
    Icon,
    HStack,
    VStack,
    Card,
    CardBody,
    Progress,
    useToast,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    IconButton,
} from '@chakra-ui/react';
import {
    LuPlus,
    LuSearch,
    LuClock,
    LuCheckCircle,
    LuAlertTriangle,
    LuExternalLink,
    LuInfo,
    LuPencil,
    LuEye,
    LuMoreVertical,
    LuPause,
    LuPlay,
} from 'react-icons/lu';

const CronManager = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isLogsOpen, onOpen: onLogsOpen, onClose: onLogsClose } = useDisclosure();
    const toast = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [cronJobs, setCronJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState({});
    const [error, setError] = useState(null);

    // Fetch cron jobs from API
    const fetchCronJobs = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/cron/list`, {
                headers: {
                    "Content-Type": "application/json",
                    "x-access-token": localStorage.getItem("user"),
                },
            });

            if (response.data) {
                setCronJobs(response.data.data || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching CRON jobs:', error);
            setError(error.response?.data?.message || 'Failed to fetch CRON jobs');

            toast({
                title: "Error",
                description: error.response?.data?.message || 'Failed to fetch CRON jobs. Please try again.',
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Load cron jobs on component mount
    useEffect(() => {
        fetchCronJobs();
    }, []);

    // Calculate stats from the actual data and API stats
    const calculateStats = () => {
        const totalJobs = cronJobs.length;
        const activeJobs = cronJobs.filter(job => job.status_name?.toLowerCase() === 'active' || job.status_id === 1).length;
        const pausedJobs = cronJobs.filter(job => job.status_name?.toLowerCase() === 'paused' || job.status_id === 2).length;

        return [
            { label: 'Total Jobs', value: totalJobs, icon: LuClock, color: 'blue' },
            { label: 'Active Jobs', value: activeJobs, icon: LuCheckCircle, color: 'green' },
            { label: 'Paused Jobs', value: pausedJobs, icon: LuAlertTriangle, color: 'orange' },
            // { label: 'Error Jobs', value: totalJobs - activeJobs - pausedJobs, icon: LuInfo, color: 'red' },
        ];
    };

    const stats = calculateStats();

    const getStatusColor = (statusName, statusId) => {
        const normalizedStatus = statusName?.toLowerCase();
        switch (normalizedStatus) {
            case 'active': return 'green';
            case 'completed': return 'green';
            case 'paused': return 'orange';
            case 'error': return 'red';
            default:
                // Fallback to status_id if status_name is not clear
                switch (statusId) {
                    case 1: return 'green'; // Active
                    case 100: return 'green'; // Active
                    case 2: return 'orange'; // Paused
                    case 3: return 'red'; // Error
                    default: return 'gray';
                }
        }
    };

    const filteredJobs = cronJobs.filter(job =>
        job.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.file?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleEditJob = (job) => {
        setSelectedJob({
            ...job,
            url: job.file, // Map file to url for the form
            status: job.status_name, // Map status_name to status for the form
            schedule: job.cron_exp // Map cron_exp to schedule for display
        });
        setIsEditing(true);
        onOpen();
    };

    const handleAddJob = () => {
        setSelectedJob(null);
        setIsEditing(false);
        onOpen();
    };

    const handleCloseModal = () => {
        setIsEditing(false);
        setSelectedJob(null);
        onClose();
    };

    const handleJobCreated = () => {
        // Refresh the jobs list after creating/updating
        fetchCronJobs();
    };

    const handleRefresh = () => {
        fetchCronJobs();
    };

    // Handle pause job
    const handlePauseJob = async (job) => {
        setActionLoading(prev => ({ ...prev, [`pause_${job.id}`]: true }));

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/cron/pause`,
                { id: job.id },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-access-token": localStorage.getItem("user"),
                    },
                }
            );

            if (response.data) {
                toast({
                    title: "CRON Job Paused",
                    description: `${job.name} has been paused successfully.`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });

                // Refresh the jobs list
                fetchCronJobs();
            }
        } catch (error) {
            console.error('Error pausing CRON job:', error);
            toast({
                title: "Error",
                description: error.response?.data?.message || 'Failed to pause CRON job. Please try again.',
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setActionLoading(prev => ({ ...prev, [`pause_${job.id}`]: false }));
        }
    };

    // Handle resume job
    const handleResumeJob = async (job) => {
        setActionLoading(prev => ({ ...prev, [`resume_${job.id}`]: true }));

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/cron/resume`,
                { id: job.id },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "x-access-token": localStorage.getItem("user"),
                    },
                }
            );

            if (response.data) {
                toast({
                    title: "CRON Job Resumed",
                    description: `${job.name} has been resumed successfully.`,
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });

                // Refresh the jobs list
                fetchCronJobs();
            }
        } catch (error) {
            console.error('Error resuming CRON job:', error);
            toast({
                title: "Error",
                description: error.response?.data?.message || 'Failed to resume CRON job. Please try again.',
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setActionLoading(prev => ({ ...prev, [`resume_${job.id}`]: false }));
        }
    };

    // Handle view logs
    const handleViewLogs = (job) => {
        setSelectedJob(job);
        onLogsOpen();
    };

    // Check if job is active
    const isJobActive = (job) => {
        return job.status_name?.toLowerCase() === 'active' || job.status_id === 1;
    };

    if (error && cronJobs.length === 0) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Alert status="error">
                        <AlertIcon />
                        <Box>
                            <AlertTitle>Error loading CRON jobs!</AlertTitle>
                            <AlertDescription>
                                {error}
                                <Button ml={4} size="sm" onClick={handleRefresh}>
                                    Try Again
                                </Button>
                            </AlertDescription>
                        </Box>
                    </Alert>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header />

            <BoxWrapper>
                <VStack spacing={6} align="stretch">
                    {/* Header */}
                    <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                            <HStack>
                                <Icon as={LuClock} boxSize={6} color="blue.500" />
                                <Heading size="lg" color="gray.800">CRON Manager</Heading>
                                {loading && <Spinner size="sm" />}
                            </HStack>
                            <Text color="gray.600">Manage scheduled HTTP requests and automated tasks</Text>
                        </VStack>
                        <HStack>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleRefresh}
                                isLoading={loading}
                            >
                                Refresh
                            </Button>
                            <Button
                                leftIcon={<LuPlus />}
                                colorScheme="blue"
                                size="sm"
                                onClick={handleAddJob}
                            >
                                Add CRON Job
                            </Button>
                        </HStack>
                    </HStack>

                    {/* Stats Cards */}
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                        {stats.map((stat, index) => (
                            <Card key={index} variant="outline">
                                <CardBody>
                                    <HStack align="center" spacing={4}>
                                        <Box
                                            bg={`${stat.color}.50`}
                                            p={3}
                                            rounded="lg"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Icon as={stat.icon} boxSize={6} color={`${stat.color}.500`} />
                                        </Box>
                                        <VStack align="start" spacing={0}>
                                            <Text color="gray.600" fontSize="sm">
                                                {stat.label}
                                            </Text>
                                            <Text fontSize="2xl" fontWeight="bold">
                                                {stat.value.toLocaleString()}
                                            </Text>
                                        </VStack>
                                    </HStack>
                                </CardBody>
                            </Card>
                        ))}
                    </SimpleGrid>

                    {/* Search */}
                    <InputGroup>
                        <InputLeftElement>
                            <Icon as={LuSearch} color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search CRON jobs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            bg="white"
                        />
                    </InputGroup>

                    {/* Jobs Table */}
                    <Card>
                        <CardBody p={0}>
                            <Heading size="md" p={4} borderBottom="1px" borderColor="gray.200">
                                CRON Jobs
                            </Heading>
                            <Box overflowX="auto">
                                {loading && cronJobs.length === 0 ? (
                                    <VStack p={8}>
                                        <Spinner size="lg" />
                                        <Text>Loading CRON jobs...</Text>
                                    </VStack>
                                ) : cronJobs.length === 0 ? (
                                    <VStack p={8}>
                                        <Text color="gray.500">No CRON jobs found</Text>
                                        <Button
                                            leftIcon={<LuPlus />}
                                            colorScheme="blue"
                                            size="sm"
                                            onClick={handleAddJob}
                                        >
                                            Create your first CRON job
                                        </Button>
                                    </VStack>
                                ) : (
                                    <Table variant="simple">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th>Job Name</Th>
                                                <Th>File</Th>
                                                <Th>Schedule</Th>
                                                <Th>Status</Th>
                                                <Th>Created At</Th>
                                                <Th>Controls</Th>
                                                <Th>Actions</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {filteredJobs.map((job) => (
                                                <Tr key={job.id}>
                                                    <Td>
                                                        <VStack align="start" spacing={1}>
                                                            <Text fontWeight="semibold">{job.name}</Text>
                                                            <Text fontSize="sm" color="gray.600">
                                                                {job.description || 'No description'}
                                                            </Text>
                                                        </VStack>
                                                    </Td>
                                                    <Td>
                                                        <HStack>
                                                            <Text
                                                                fontSize="sm"
                                                                color="blue"
                                                                maxW="200px"
                                                                isTruncated
                                                            >
                                                                {job.file}
                                                            </Text>
                                                            <Icon as={LuExternalLink} boxSize={3} color="gray.400" />
                                                        </HStack>
                                                    </Td>
                                                    <Td>
                                                        <VStack align="start" spacing={1}>
                                                            <Text fontSize="sm">{job.cron_exp}</Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                {job.schedule_type === 's' ? 'Standard' : 'Custom'}
                                                            </Text>
                                                        </VStack>
                                                    </Td>
                                                    <Td>
                                                        <Tag
                                                            colorScheme={getStatusColor(job.status_name, job.status_id)}
                                                           
                                                        >
                                                            {job.status_name || 'Unknown'}
                                                        </Tag>
                                                    </Td>
                                                    <Td>
                                                        <VStack align="start" spacing={1}>
                                                            <Text fontSize="sm">
                                                                {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}
                                                            </Text>
                                                            <Text fontSize="xs" color="gray.500">
                                                                {job.created_at ? new Date(job.created_at).toLocaleTimeString() : ''}
                                                            </Text>
                                                        </VStack>
                                                    </Td>
                                                    <Td>
                                                        {job.status_id !== 100 ? <HStack spacing={2}>
                                                            {isJobActive(job) ? (
                                                                <IconButton
                                                                    aria-label="Pause job"
                                                                    icon={actionLoading[`pause_${job.id}`] ? <Spinner size="sm" /> : <LuPause />}
                                                                    size="sm"
                                                                    colorScheme="orange"
                                                                    variant="ghost"
                                                                    onClick={() => handlePauseJob(job)}
                                                                    isDisabled={actionLoading[`pause_${job.id}`]}
                                                                    title="Pause Job"
                                                                />
                                                            ) : (
                                                                <IconButton
                                                                    aria-label="Resume job"
                                                                    icon={actionLoading[`resume_${job.id}`] ? <Spinner size="sm" /> : <LuPlay />}
                                                                    size="sm"
                                                                    colorScheme="green"
                                                                    variant="ghost"
                                                                    onClick={() => handleResumeJob(job)}
                                                                    isDisabled={actionLoading[`resume_${job.id}`]}
                                                                    title="Resume Job"
                                                                />
                                                            )}
                                                        </HStack> : "-"}

                                                    </Td>
                                                    <Td>

                                                        <Menu>
                                                            <MenuButton
                                                                as={IconButton}
                                                                aria-label="Options"
                                                                icon={<LuMoreVertical />}
                                                                variant="ghost"
                                                                size="sm"
                                                            />
                                                            <MenuList>
                                                                {job.status_id !== 100 ? <MenuItem
                                                                    icon={<LuPencil />}
                                                                    onClick={() => handleEditJob(job)}
                                                                >
                                                                    Edit Job
                                                                </MenuItem> : ""}

                                                                <MenuItem
                                                                    icon={<LuEye />}
                                                                    onClick={() => handleViewLogs(job)}
                                                                >
                                                                    View Logs
                                                                </MenuItem>
                                                            </MenuList>
                                                        </Menu>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                )}
                            </Box>
                        </CardBody>
                    </Card>
                </VStack>
            </BoxWrapper>

            {/* CRON Job Form Modal */}
            <CronJobForm
                isOpen={isOpen}
                onClose={handleCloseModal}
                isEditing={isEditing}
                selectedJob={selectedJob}
                onJobCreated={handleJobCreated}
            />

            {/* View Logs Modal */}
            <Modal isOpen={isLogsOpen} onClose={onLogsClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>CRON Job Details - {selectedJob?.name}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedJob && (
                            <VStack spacing={4} align="stretch">
                                <VStack spacing={3} align="stretch">
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Job ID:</Text>
                                        <Text>{selectedJob.id}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Name:</Text>
                                        <Text>{selectedJob.name}</Text>
                                    </HStack>
                                    <HStack justify="space-between" align="start">
                                        <Text fontWeight="bold" color="gray.600">Description:</Text>
                                        <Text textAlign="right" maxW="300px">{selectedJob.description || 'No description'}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">File:</Text>
                                        <Text color="blue.600">{selectedJob.file}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">CRON Expression:</Text>
                                        <Text fontFamily="mono" bg="gray.100" px={2} py={1} rounded="md">{selectedJob.cron_exp}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Schedule Type:</Text>
                                        <Tag colorScheme={selectedJob.schedule_type === 's' ? 'blue' : 'purple'}>
                                            {selectedJob.schedule_type === 's' ? 'Standard' : 'Custom'}
                                        </Tag>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Status:</Text>
                                        <Tag colorScheme={getStatusColor(selectedJob.status_name, selectedJob.status_id)}>
                                            {selectedJob.status_name}
                                        </Tag>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Created By:</Text>
                                        <Text>{selectedJob.created_by || 'System'}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Created At:</Text>
                                        <Text>{new Date(selectedJob.created_at).toLocaleString()}</Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" color="gray.600">Last Updated:</Text>
                                        <Text>{new Date(selectedJob.updated_at).toLocaleString()}</Text>
                                    </HStack>
                                </VStack>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onLogsClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default CronManager;